import { toApiError } from '@/lib/http';

/**
 * Finding khutbahs: this Friday's, a month's worth, or a search.
 */

export interface LanguageState {
  readonly code: string;
  readonly name: string;
  readonly state: 'reviewed' | 'unreviewed';
}

export interface KhutbahCard {
  readonly khutbahId: string;
  readonly friday: string;
  readonly institutionKey: string;
  readonly institutionName: string;
  readonly institutionNameAr: string | null;
  readonly verifiedPublisher: boolean;
  readonly sourceKind: 'publisher' | 'user';
  readonly titleOriginal: string | null;
  /**
   * The same title in the requested language, or null when nobody has
   * published one. Null is the honest answer and the interface shows the
   * Arabic alone rather than inventing a rendering of it.
   */
  readonly titleTranslated: string | null;
  readonly occasion: string;
  readonly languages: readonly LanguageState[];
}

export interface FridayEntry {
  readonly friday: string;
  readonly institutions: readonly string[];
}

function str(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

function parseCard(value: unknown): KhutbahCard[] {
  if (typeof value !== 'object' || value === null) return [];
  const card = value as Record<string, unknown>;
  const khutbahId = str(card, 'khutbahId');
  const friday = str(card, 'friday');
  if (khutbahId === null || friday === null) return [];
  return [
    {
      khutbahId,
      friday,
      institutionKey: str(card, 'institutionKey') ?? '',
      institutionName: str(card, 'institutionName') ?? '',
      institutionNameAr: str(card, 'institutionNameAr'),
      verifiedPublisher: card.verifiedPublisher === true,
      sourceKind: card.sourceKind === 'user' ? 'user' : 'publisher',
      titleOriginal: str(card, 'titleOriginal'),
      titleTranslated: str(card, 'titleTranslated'),
      occasion: str(card, 'occasion') ?? 'friday',
      languages: Array.isArray(card.languages)
        ? card.languages.flatMap((entry): LanguageState[] => {
            if (typeof entry !== 'object' || entry === null) return [];
            const language = entry as Record<string, unknown>;
            const code = str(language, 'code');
            if (code === null) return [];
            return [
              {
                code,
                name: str(language, 'name') ?? code,
                // Defaults to unreviewed: claiming a translation is reviewed
                // when the server did not say so is the one direction this
                // must never get wrong.
                state: language.state === 'reviewed' ? 'reviewed' : 'unreviewed',
              },
            ];
          })
        : [],
    },
  ];
}

export async function fetchKhutbahs(
  params: {
    from?: string;
    to?: string;
    institution?: string;
    language?: string;
    q?: string;
    /**
     * Which language to show each title in. Separate from `language`, which
     * filters to khutbahs that HAVE a language — asking for English titles
     * must not silently drop every khutbah not yet translated into English.
     */
    titleLanguage?: string;
    /** The endpoint's own bound; it defaults to 20 and caps the page. */
    limit?: number;
  },
  signal: AbortSignal,
): Promise<KhutbahCard[]> {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, String(value));
  }
  const response = await fetch(`/api/public/khutbahs?${search}`, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw await toApiError(response);
  }
  const body: unknown = await response.json();
  return Array.isArray(body) ? body.flatMap(parseCard) : [];
}

/**
 * How many ids one request may carry.
 *
 * The server's own bound, mirrored here so the client can batch rather than
 * discover it as a 400. Kept deliberately equal: a smaller number would make
 * needless requests, a larger one would start failing.
 */
const ID_BATCH = 60;

/**
 * Specific khutbahs by id, for the ids this browser holds.
 *
 * Saved items, reading history and a khutbah named in a link are ids the
 * server knows nothing about. Resolving them against the newest page of the
 * catalogue lost everything older than that page; this asks for them by
 * name. The server applies the same publication gate a browse gets, so an
 * id is not a way to reach something unpublished.
 */
export async function fetchKhutbahsByIds(
  ids: readonly string[],
  signal: AbortSignal,
  titleLanguage = 'en',
): Promise<KhutbahCard[]> {
  if (ids.length === 0) return [];

  // Batched, because the endpoint bounds a request and now refuses one that
  // exceeds it rather than trimming in silence. Sending the whole union of
  // history and saved ids lost everything past the bound: a library of 61
  // kept khutbahs rendered 60 and counted 61, with nothing to say an answer
  // was incomplete.
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += ID_BATCH) {
    batches.push([...ids].slice(i, i + ID_BATCH));
  }

  // In parallel: they are independent reads, and a long library should not
  // take one round trip per sixty khutbahs in series. The signal is passed
  // through to every one, so switching away cancels them all.
  const pages = await Promise.all(
    batches.map(async (batch) => {
      const search = new URLSearchParams({ ids: batch.join(','), titleLanguage });
      const response = await fetch(`/api/public/khutbahs/by-id?${search}`, {
        signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw await toApiError(response);
      }
      const body: unknown = await response.json();
      return Array.isArray(body) ? body.flatMap(parseCard) : [];
    }),
  );

  return pages.flat();
}

export async function fetchCalendar(
  from: string,
  to: string,
  signal: AbortSignal,
): Promise<FridayEntry[]> {
  const response = await fetch(
    `/api/public/calendar?from=${from}&to=${to}`,
    { signal, headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw await toApiError(response);
  }
  const body: unknown = await response.json();
  if (!Array.isArray(body)) return [];
  return body.flatMap((entry): FridayEntry[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const row = entry as Record<string, unknown>;
    const friday = str(row, 'friday');
    if (friday === null) return [];
    return [
      {
        friday,
        institutions: Array.isArray(row.institutions)
          ? row.institutions.filter((i): i is string => typeof i === 'string')
          : [],
      },
    ];
  });
}
